import React from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useI18n } from '../../i18n'
import type { OperationalSummaryRow } from '../../services/informationSystemOperationalApi'
import type { InformationSystemExecutiveSummary } from '../../services/enterpriseOperationalUxApi'
import type { DrillDownTarget } from './OperationalDrillDownPanel'
import {
  computeRemediationPriority,
  deriveSecurityHealthStatus,
  deriveSystemSecurityHealthPercent,
} from '../../utils/informationSystemRemediationRanking'
import styles from '../../styles/components/assets/AssetsInformationSystems.module.scss'

type Props = {
  row: OperationalSummaryRow
  executiveSummary?: InformationSystemExecutiveSummary | null
  selected?: boolean
  expanded?: boolean
  onToggleExpand: () => void
  onSelect: () => void
  onOpenPassport: () => void
  onOpenGraph: () => void
  onOpenEvidence: () => void
  onOpenRemediation: () => void
  onOpenControls: () => void
  onOpenReadiness: () => void
  onOpenCriticalData: () => void
  onOpenStandards: () => void
  onOpenExecutive?: () => void
  onDrillDown: (target: DrillDownTarget) => void
  onEdit?: () => void
  onArchive?: () => void
  onDelete?: () => void
}

function bandClass(band: string): string {
  const b = String(band || '').toUpperCase()
  if (b === 'CRITICAL') return styles.priorityBandCritical
  if (b === 'HIGH') return styles.priorityBandHigh
  if (b === 'MEDIUM') return styles.priorityBandMedium
  return styles.priorityBandLow
}

export default function InformationSystemCompactListRow({
  row,
  executiveSummary,
  selected,
  expanded,
  onToggleExpand,
  onSelect,
  onOpenPassport,
  onOpenGraph,
  onOpenEvidence,
  onOpenRemediation,
  onOpenControls,
  onOpenReadiness,
  onOpenCriticalData,
  onOpenStandards,
  onOpenExecutive,
  onDrillDown,
  onEdit,
  onArchive,
  onDelete,
}: Props) {
  const { t } = useI18n()
  const systemId = row.informationSystemId
  const priority =
    row.remediationPriority ||
    computeRemediationPriority(row, executiveSummary || null)
  const health =
    row.securityHealthPercent != null
      ? row.securityHealthPercent
      : deriveSystemSecurityHealthPercent(row)
  const healthStatus =
    row.securityHealthStatus || deriveSecurityHealthStatus(health) || '—'
  const problemCount = Math.max(
    Number(priority.problemCount || 0),
    (row.topVulnerabilities || []).length +
      (row.topFailedControls || []).length +
      (row.topOpenRisks || []).length,
  )

  const stop = (e: React.SyntheticEvent) => e.stopPropagation()

  return (
    <article
      className={`${styles.compactIsRow} ${selected ? styles.compactIsRowSelected : ''} ${expanded ? styles.compactIsRowExpanded : ''}`.trim()}
      data-testid="is-compact-row"
      data-system-id={systemId}
    >
      <div
        className={styles.compactIsSummary}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={row.name}
        onClick={onToggleExpand}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggleExpand()
          }
        }}
      >
        <button
          type="button"
          className={styles.compactExpandBtn}
          aria-label={expanded ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}
          onClick={(e) => {
            e.stopPropagation()
            onToggleExpand()
          }}
        >
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>

        <div className={styles.compactIsIdentity}>
          <p className={styles.compactIsName} title={row.name || undefined}>
            {row.name || t('common.unnamed', 'Unnamed')}
          </p>
          {row.code ? <p className={styles.compactIsCode}>{row.code}</p> : null}
        </div>

        <span className={styles.compactMetaChip} title={t('assets.lifecycle.status', 'Lifecycle')}>
          {row.lifecycleStatus || '—'}
        </span>
        <span
          className={styles.compactMetaChip}
          title={t('assets.criticality', 'Business criticality (passport)')}
        >
          {row.criticality || '—'}
        </span>
        <span
          className={`${styles.compactMetaChip} ${styles.compactProblemsChip}`}
          title={t(
            'assets.informationSystems.priorityProblems',
            'Open items (vulns + risks + failed controls + blockers)',
          )}
        >
          {problemCount}
        </span>
        <span
          className={`${styles.compactMetaChip} ${bandClass(priority.band)}`}
          title={t(
            'assets.informationSystems.remediationPriorityHint',
            'Fix priority for triage — not Total Security Health / official Risk Engine score',
          )}
        >
          {t('assets.informationSystems.fixPrioShort', 'Fix')}: {priority.band}
        </span>
        <span
          className={styles.compactHealth}
          title={t(
            'assets.informationSystems.securityHealthHint',
            'Security Health = same formula as Home Total Security Health (vuln severity penalty). Not control coverage.',
          )}
          data-testid="is-security-health"
        >
          <span className={styles.compactHealthPct}>{health}%</span>
          <span className={styles.compactHealthStatus}>{healthStatus}</span>
        </span>
      </div>

      {expanded ? (
        <div className={styles.compactIsDetails} data-testid="is-compact-details">
          <dl className={styles.compactDetailGrid}>
            <div>
              <dt>{t('assets.postureRow.owner', 'Owner')}</dt>
              <dd>{row.ownerLabel || t('common.notSet', 'Not set')}</dd>
            </div>
            <div>
              <dt>{t('assets.environment', 'Environment')}</dt>
              <dd>{row.environment || '—'}</dd>
            </div>
            <div>
              <dt>{t('assets.postureRow.criticalData', 'Critical data')}</dt>
              <dd>
                {(row.criticalDataCategories || []).length
                  ? (row.criticalDataCategories || []).join(', ')
                  : t('assets.postureRow.noCriticalData', 'No critical data mapped')}
              </dd>
            </div>
            <div>
              <dt>{t('assets.postureRow.standards', 'Standards')}</dt>
              <dd>
                {(row.applicableStandards || []).length
                  ? (row.applicableStandards || []).join(', ')
                  : t('assets.postureRow.noStandards', 'No standards scoped')}
              </dd>
            </div>
            <div>
              <dt>{t('assets.informationSystems.securityHealth', 'Security Health')}</dt>
              <dd>
                {health}% · {healthStatus}
                <span className={styles.compactHealthNote}>
                  {' '}
                  (
                  {t(
                    'assets.informationSystems.tshFormulaShort',
                    'TSH = 100 − (C×4+H×3+M×2+L×1)',
                  )}
                  )
                </span>
              </dd>
            </div>
            {row.controlCoveragePercent != null || row.readiness?.controlCoveragePercent != null ? (
              <div>
                <dt>{t('assets.informationSystems.controlCoverage', 'Control coverage')}</dt>
                <dd>
                  {Math.round(
                    Number(
                      row.controlCoveragePercent ?? row.readiness?.controlCoveragePercent ?? 0,
                    ),
                  )}
                  %
                  <span className={styles.compactHealthNote}>
                    {' '}
                    ({t('assets.informationSystems.notTsh', 'not Security Health')})
                  </span>
                </dd>
              </div>
            ) : null}
          </dl>

          <div className={styles.compactDetailSections}>
            <section>
              <h4>{t('assets.postureRow.topVulnerabilities', 'Top vulnerabilities')}</h4>
              {(row.topVulnerabilities || []).length === 0 ? (
                <p className={styles.emptyCell}>
                  {t('assets.postureRow.noVulnerabilities', 'No critical vulnerabilities')}
                </p>
              ) : (
                <ul className={styles.compactIssueList}>
                  {(row.topVulnerabilities || []).slice(0, 3).map((v) => (
                    <li key={v.id}>
                      <button
                        type="button"
                        className={styles.issueChip}
                        onClick={(e) => {
                          stop(e)
                          onDrillDown({
                            kind: 'vulnerability',
                            systemId,
                            id: v.id,
                            label: v.title,
                          })
                        }}
                      >
                        <strong>{v.severity}</strong> {v.cve || v.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section>
              <h4>{t('assets.postureRow.openRisks', 'Open risks')}</h4>
              {(row.topOpenRisks || []).length === 0 ? (
                <p className={styles.emptyCell}>{t('assets.postureRow.noOpenRisks', 'No open risks')}</p>
              ) : (
                <ul className={styles.compactIssueList}>
                  {(row.topOpenRisks || []).slice(0, 3).map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        className={styles.issueChip}
                        onClick={(e) => {
                          stop(e)
                          onDrillDown({
                            kind: 'risk',
                            systemId,
                            sourceType: r.sourceType || '',
                            sourceId: r.sourceId || r.id,
                            label: r.title,
                          })
                        }}
                      >
                        {r.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section>
              <h4>{t('assets.postureRow.failedControls', 'Failed controls')}</h4>
              {(row.topFailedControls || []).length === 0 ? (
                <p className={styles.emptyCell}>
                  {t('assets.postureRow.noFailedControls', 'No failed controls')}
                </p>
              ) : (
                <ul className={styles.compactIssueList}>
                  {(row.topFailedControls || []).slice(0, 3).map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className={styles.issueChip}
                        onClick={(e) => {
                          stop(e)
                          onDrillDown({
                            kind: 'control',
                            systemId,
                            id: c.id,
                            label: c.controlTitle,
                          })
                        }}
                      >
                        {c.controlCode || c.controlTitle}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section>
              <h4>{t('assets.informationSystems.complianceGaps', 'Compliance gaps')}</h4>
              {(() => {
                const gaps = (row.complianceScope || []).filter(
                  (s) =>
                    s.available !== false &&
                    ((s.verifiedPercent != null && Number(s.verifiedPercent) < 40) ||
                      (s.verifiedPercent == null &&
                        s.selfAssessedPercent != null &&
                        Number(s.selfAssessedPercent) < 40)),
                )
                if (!gaps.length) {
                  return (
                    <p className={styles.emptyCell}>
                      {t(
                        'assets.informationSystems.noConfirmedComplianceGaps',
                        'No confirmed compliance gaps (not assessed ≠ violation)',
                      )}
                    </p>
                  )
                }
                return (
                  <ul className={styles.compactIssueList}>
                    {gaps.slice(0, 4).map((g) => (
                      <li key={g.standardCode}>
                        {g.standardCode}:{' '}
                        {g.verifiedPercent != null
                          ? `${Math.round(Number(g.verifiedPercent))}% verified`
                          : `${Math.round(Number(g.selfAssessedPercent))}% self`}
                      </li>
                    ))}
                  </ul>
                )
              })()}
            </section>
          </div>

          <div className={styles.compactPriorityExplain} data-testid="is-fix-priority-explain">
            <p>
              <strong>{t('assets.informationSystems.fixPriority', 'Fix priority')}</strong>:{' '}
              {priority.band}
              {priority.baseBand && priority.baseBand !== priority.band
                ? ` (${t('assets.informationSystems.baseBand', 'base')} ${priority.baseBand})`
                : ''}
            </p>
            {priority.primaryFactor ? (
              <p>
                <strong>{t('assets.informationSystems.primaryFactor', 'Primary')}</strong>:{' '}
                {priority.primaryFactor.detail}
                {priority.primaryFactor.source ? ` [${priority.primaryFactor.source}]` : ''}
              </p>
            ) : null}
            {(priority.contextFactors || []).length ? (
              <p>
                <strong>{t('assets.informationSystems.contextFactors', 'Context')}</strong>:{' '}
                {(priority.contextFactors || [])
                  .slice(0, 4)
                  .map((f) => f.id)
                  .join(', ')}
                {(priority.contextFactors || []).length > 4 ? '…' : ''}
              </p>
            ) : null}
            {priority.escalation ? (
              <p>
                <strong>{t('assets.informationSystems.escalation', 'Escalation')}</strong>:{' '}
                {priority.escalation.fromBand} → {priority.escalation.toBand} —{' '}
                {priority.escalation.reason}
                {priority.escalation.source ? ` (${priority.escalation.source})` : ''}
              </p>
            ) : (
              <p className={styles.compactHealthNote}>
                {t(
                  'assets.informationSystems.noEscalation',
                  'No severity escalation — business criticality and exposure adjust sort order only',
                )}
              </p>
            )}
          </div>

          <div className={styles.compactActions} onClick={stop}>
            <button type="button" className={styles.rowActionBtn} onClick={onSelect}>
              {t('assets.informationSystems.openOverview', 'Overview')}
            </button>
            <button type="button" className={styles.rowActionBtn} onClick={onOpenPassport}>
              {t('assets.passport.title', 'Passport')}
            </button>
            <button type="button" className={styles.rowActionBtn} onClick={onOpenGraph}>
              {t('assets.graph.title', 'Graph')}
            </button>
            <button type="button" className={styles.rowActionBtn} onClick={onOpenEvidence}>
              {t('assets.evidence.title', 'Evidence')}
            </button>
            <button type="button" className={styles.rowActionBtn} onClick={onOpenRemediation}>
              {t('assets.remediation.title', 'Remediation')}
            </button>
            <button type="button" className={styles.rowActionBtn} onClick={onOpenControls}>
              {t('assets.controls.title', 'Controls')}
            </button>
            <button type="button" className={styles.rowActionBtn} onClick={onOpenReadiness}>
              {t('assets.readiness.title', 'Readiness')}
            </button>
            <button type="button" className={styles.rowActionBtn} onClick={onOpenCriticalData}>
              {t('assets.postureRow.criticalData', 'Critical data')}
            </button>
            <button type="button" className={styles.rowActionBtn} onClick={onOpenStandards}>
              {t('assets.postureRow.standards', 'Standards')}
            </button>
            {onOpenExecutive ? (
              <button type="button" className={styles.rowActionBtn} onClick={onOpenExecutive}>
                {t('enterpriseOperationalUx.executive', 'Executive')}
              </button>
            ) : null}
            {onEdit ? (
              <button type="button" className={styles.rowActionBtn} onClick={onEdit}>
                {t('common.edit', 'Edit')}
              </button>
            ) : null}
            {onArchive ? (
              <button type="button" className={styles.rowActionBtn} onClick={onArchive}>
                {t('common.archive', 'Archive')}
              </button>
            ) : null}
            {onDelete ? (
              <button type="button" className={`${styles.rowActionBtn} ${styles.rowActionDanger}`} onClick={onDelete}>
                {t('common.delete', 'Delete')}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  )
}
